import React, { useContext, useMemo, useState } from "react";
import { Button, Dropdown, Icon, Menu, message, Spin, Tree } from "antd";
import TreeItem from "./Components/TreeItem";
import Style from "./index.module.scss";
import QueteModal from "./Components/QuoteModal";
import useModalHandler from "../../../../utils/v2/useModalHandler";
import CopyModal from "./Components/CopyModal";
import MoveModal, { moveProps } from "./Components/MoveModal";
import NodeModal from "./Components/NodeModal";
import { CourseContext } from "../../Store";
import useRequest from "../../../../utils/useRequest";
import {
    ChapterTree,
    deleteChapterAndCourseware,
    getChapter,
    getChapterProp,
    moveChapter,
    updateChapter,
    updateCoursewareSource,
    isChapter,
    isFile,
    moveChapterOrCourseware,
    MoveChapterProps,
    getTreeDragProps,
    getMaxFloor,
    getMax
} from "../../../../service/CourseDetail/chapter";
import useCurrentDate from "../../../../utils/useCurrentDate";
import useRequestFn from "../../../../utils/useRequestFn";
import { ResultMessage } from "../../../../components/ResultMessage";
import { errorMessage } from "../../../../service/service";
import FileItem from "../../../../components/FileItem";
import {
    getSourceList,
    nodeType,
    SourceItem
} from "../../../ResourcePool/service";
import style from "../../Resource/index.module.scss";
import PreviewSourceModal from "../../../../components/PreviewSourceModal";
import {
    AntTreeNodeDragEnterEvent,
    AntTreeNodeDropEvent
} from "antd/es/tree/Tree";
import DeleteConfirm from "../../../../components/DeleteConfirm";
import TagStatus from "../../../../components/CourseComponents/Resource/ListItem/Components/TagStatus";
import lodash from "lodash-es";
import ApplyModal from "./Components/ApplyModal";

const { TreeNode } = Tree;

const Index = () => {
    const { course } = useContext(CourseContext);
    const { batchNo } = useCurrentDate();
    const nodeParams: getChapterProp = {
        dcCoursewareSourceParam: {
            delStatus: ""
        },
        dcCourseChapterEntity: {
            batchNo,
            delStatus: "",
            courseNo: course.courseNo,
            courseSeq: course.courseSeq
        }
    };
    const [treeData, { loading: treeLoading }, refreshTree] = useRequest(() =>
        getChapter(nodeParams)
    );

    const [SourceList, { loading: SourceLoading }, reloadSource] = useRequest(
        () =>
            getSourceList({
                batchNo,
                courseNo: course.courseNo,
                courseSeq: course.courseSeq,
                delStatus: "",
                keyword: "",
                pageNo: 1,
                pageSize: 9999
            })
    );
    const [editAction, { loading: editLoading }] = useRequestFn(updateChapter);
    const [deleteAction, { loading: deleteLoading }] = useRequestFn(
        deleteChapterAndCourseware
    );

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    async function reloadAll() {
        await refreshTree();
        await reloadSource();
    }

    async function editNode(
        id: number,
        parentId: number,
        chapterName?: string
    ) {
        try {
            await editAction({
                chapterName: chapterName || "请重命名。",
                parentId,
                id
            });
            await reloadAll();
            message.success("编辑成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    async function deleteNode(chapterId: string[], coursewareId: string[]) {
        try {
            await setSelectKeys([]);
            await deleteAction({
                batchNo: batchNo,
                courseNo: course.courseNo,
                courseSeq: course.courseSeq,
                chapterId: chapterId.join(","),
                coursewareId: coursewareId.join(",")
            });
            await reloadAll();
            message.success("删除成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    const [moveFileAction, { loading: moveFileLoading }] = useRequestFn(
        updateCoursewareSource
    );
    async function moveFile(id: string, chapterId: number) {
        try {
            await moveFileAction({
                chapterId,
                id
            });
            await reloadAll();
            setSelectKeys([]);
            message.success("课件移动成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    const [moveNodeAction, { loading: moveNodeLoading }] =
        useRequestFn(moveChapter);
    async function moveNode(id: number, chapterId: number) {
        try {
            await moveNodeAction({
                parentId: chapterId,
                id
            });
            await reloadAll();
            message.success("章节移动成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    //存储key value 避免日后再次递归获取数据,构造树结构时直接插入
    const nodeMap = useMemo(() => {
        return new Map<string, ChapterTree | SourceItem>();
    }, []);

    function renderTreeNodes(data: ChapterTree[]) {
        return data.map(item => {
            const nodeKey = `NODE_${item.id}`;
            item.key = nodeKey;
            nodeMap.set(nodeKey, item);

            const nodes = (
                <TreeItem
                    onMove={() => setMoveModal(item, item.key)}
                    onAppend={parentId => {
                        setNodeModal(parentId.toString());
                    }}
                    onDelete={item => {
                        onDeleteOne(item.key);
                    }}
                    onEdit={(id, parentId, chapterName) => {
                        editNode(id, parentId, chapterName);
                    }}
                    data={item}
                />
            );
            const fileNodes = item.dcCoursewareSourceList.map(source => {
                const fileKey = `FILE_${source.id}`;
                source.key = fileKey;
                nodeMap.set(fileKey, source);
                return (
                    <TreeNode
                        key={source.key}
                        title={
                            <FileItem
                                nameWidth="70%"
                                nameClick={() => onPrev(source?.id)}
                                text={source.sourceFileEntity.fileName}
                                title={<TagStatus state={source?.isPublish} />}
                                fileType={source?.sourceFileEntity?.fileType}>
                                <div
                                    onClick={() =>
                                        setMoveModal(item, source.key)
                                    }>
                                    <Button type="link" size="small">
                                        移动
                                    </Button>
                                </div>
                                <div>
                                    <DeleteConfirm
                                        title="确定删除吗？"
                                        content={`文件名称：${source.sourceFileEntity.fileName}`}
                                        onSuccess={() => {
                                            onDeleteOne(source.key);
                                        }}>
                                        <Button type="link" size="small">
                                            删除
                                        </Button>
                                    </DeleteConfirm>
                                </div>
                            </FileItem>
                        }
                    />
                );
            });
            if (item.children.length > 0) {
                return (
                    <TreeNode
                        title={nodes}
                        key={item.key}
                        dataRef={item}
                        checkable>
                        {renderTreeNodes(item.children).concat(fileNodes)}
                    </TreeNode>
                );
            } else {
                return (
                    <React.Fragment key={item.id.toString()}>
                        <TreeNode key={item.key} title={nodes} checkable>
                            {fileNodes}
                        </TreeNode>
                    </React.Fragment>
                );
            }
        });
    }

    const [selectKeys, setSelectKeys] = useState<string[]>([]);

    const menu = (
        <Menu>
            <Menu.Item
                onClick={() => {
                    setNodeModal("0");
                }}>
                添加子级
            </Menu.Item>
            <Menu.Item onClick={setQuoteModal}>引用章节</Menu.Item>
            <Menu.Item onClick={setCopyModal}>复制章节</Menu.Item>
        </Menu>
    );

    async function onQuoteOk() {
        quoteModal.close();
        await reloadAll();
    }

    const quoteModal = useModalHandler(QueteModal, { remove: true });

    function setQuoteModal() {
        quoteModal.open();
    }
    const copyModal = useModalHandler(CopyModal);
    async function onCopyOk() {
        copyModal.close();
        await reloadAll();
    }
    function setCopyModal() {
        copyModal.open();
    }

    const applyModal = useModalHandler(ApplyModal, { remove: true });
    async function onApplyOk() {
        applyModal.close();
        await reloadAll();
    }
    function setApplyModal() {
        applyModal.open();
    }

    const moveModal = useModalHandler(MoveModal);

    function moveMany() {
        const selectLeafs = selectKeys.filter(isLeafNode);
        setMoveModal(null, selectLeafs.join(",").replaceAll("FILE_", ""));
    }

    // 判断当前节点id是否属于课件节点
    function isLeafNode(key: string) {
        const data = nodeMap.get(key);
        return data?.nodeType === nodeType.Courseware;
    }

    async function onMoveOk({ nodeId, targetKey }: moveProps) {
        setSelectedKeys([]);
        const item = nodeMap.get(targetKey);
        if (isFile(item)) {
            await moveFile(item.id.toString(), parseInt(nodeId));
        } else if (isChapter(item)) {
            await moveNode(item.id, parseInt(nodeId));
        } else {
            await moveFile(targetKey, parseInt(nodeId));
        }
        moveModal.close();
    }
    //params 所在真实节点数据 点击目标key
    function setMoveModal(payload: ChapterTree | null, targetKey: string) {
        setSelectedKeys([targetKey]);
        moveModal.open();
        moveModal.setPayload({ payload, targetKey });
    }

    const nodeModal = useModalHandler(NodeModal);
    async function onNodeOk() {
        nodeModal.close();
        await reloadAll();
    }
    function setNodeModal(parentId: string) {
        nodeModal.open();
        nodeModal.setPayload({ parentId });
    }

    async function onDeleteOne(key: string) {
        const [a, b] = key.includes("NODE")
            ? [[key.split("_")[1]], []]
            : [[], [key.split("_")[1]]];
        await deleteNode(a, b);
    }

    async function onDeleteMany() {
        const _data = lodash.cloneDeep(selectKeys);
        const a: string[] = [];
        const b: string[] = [];
        _data.forEach(key => {
            if (key.includes("NODE")) {
                a.push(key.split("_")[1]);
            } else {
                b.push(key.split("_")[1]);
            }
        });
        const _a = a.filter(item => {
            return item !== "0";
        });

        await deleteNode(_a, b);
    }

    const loading = useMemo(() => {
        return [
            treeLoading,
            editLoading,
            deleteLoading,
            moveNodeLoading,
            moveFileLoading
        ].some(item => item);
    }, [
        treeLoading,
        editLoading,
        deleteLoading,
        moveNodeLoading,
        moveFileLoading
    ]);

    //预览
    const prevModal = useModalHandler(PreviewSourceModal);
    function onPrev(id: number) {
        prevModal.setPayload(id);
        prevModal.open();
    }

    //进入
    function onDragEnter({ event, node }: AntTreeNodeDragEnterEvent) {
        return false;
    }

    const [moveAction, { loading: moveLoading }] = useRequestFn(
        moveChapterOrCourseware
    );

    async function moveFun(data: MoveChapterProps) {
        try {
            await moveAction(data);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    //释放
    async function onDrop({
        event,
        node,
        dragNode,
        dropToGap,
        dropPosition,
        dragNodesKeys
    }: AntTreeNodeDropEvent) {
        const form = dragNode.props.eventKey;
        const formNode = nodeMap.get(form as string);

        const target = node.props.eventKey;
        const targetNode = nodeMap.get(target as string);
        if (
            !targetNode ||
            !formNode ||
            !treeData ||
            treeData.length < 1 ||
            formNode?.id === targetNode?.id
        ) {
            return false;
        }

        //移动到缝隙  position 目标节点-1 上 +1 下
        if (dropToGap) {
            await moveFun(
                getTreeDragProps({
                    targetNode,
                    dropPosition,
                    treeData,
                    formNode
                })
            );
            await reloadAll();
        } else {
            if (isFile(targetNode)) {
                return;
            }
            //目标章节
            if (isChapter(targetNode)) {
                //来源文件
                if (isFile(formNode)) {
                    moveFile(formNode.id.toString(), targetNode.id);
                }

                //来源章节
                if (isChapter(formNode)) {
                    const formLevel = getMaxFloor([formNode]); //获取点击的node节点的层级
                    const disabled =
                        targetNode.level + formLevel >= getMax(form);
                    if (!disabled) {
                        moveNode(formNode.id, targetNode.id);
                    }
                }
            }
        }
    }

    return (
        <Spin spinning={loading || SourceLoading || moveLoading}>
            <div>
                <div className={Style.tools}>
                    <a
                        href="http://go.plvideo.cn/front/video/view?vid=5c0ad4c56c28b5e1ee07aada4f917d90_5"
                        target="_blank"
                        className={style.tip}
                        rel="noreferrer">
                        <Icon type="play-square" /> 点击查看使用说明视频
                    </a>
                    <div className={Style.toolsRight}>
                        <DeleteConfirm
                            title="确定删除吗？"
                            content="如果选中任一章节节点并选择确定删除，那么该章节节点下的所有课件将一并被删除；如果仅选中任一章节节点下的课件并选择确定删除，那么仅会删除被选中的课件。确定删除后，除直播回放外的课件会进入“我的资源库-回收站”中，您确定要删除吗？"
                            onSuccess={onDeleteMany}>
                            <Button
                                type="danger"
                                disabled={selectKeys.length === 0}>
                                批量删除
                            </Button>
                        </DeleteConfirm>
                        <Button
                            disabled={selectKeys.length === 0}
                            onClick={moveMany}>
                            批量移动
                        </Button>
                        <Button type="primary" onClick={setApplyModal}>
                            应用到其他课程
                        </Button>
                        <Dropdown overlay={menu} placement="bottomCenter">
                            <Button>
                                添加章节
                                <Icon type="down" />
                            </Button>
                        </Dropdown>
                    </div>
                </div>
                {treeData && treeData.length > 0 && (
                    <Tree
                        checkedKeys={selectKeys}
                        onCheck={(checkedKeys, e) => {
                            setSelectKeys(checkedKeys as string[]);
                        }}
                        selectedKeys={selectedKeys}
                        checkable
                        defaultExpandAll
                        blockNode
                        draggable
                        onDragEnter={onDragEnter}
                        onDrop={onDrop}>
                        {renderTreeNodes(treeData)}
                    </Tree>
                )}

                {quoteModal.render(<QueteModal onOk={onQuoteOk} width={610} />)}
                {copyModal.render(<CopyModal onOk={onCopyOk} width={610} />)}
                {moveModal.render(
                    <MoveModal
                        onCancel={() => {
                            setSelectedKeys([]);
                        }}
                        treeData={treeData || []}
                        width={610}
                        onSuccess={onMoveOk}
                    />
                )}
                {nodeModal.render(
                    <NodeModal
                        treeData={treeData || []}
                        onOk={onNodeOk}
                        width={610}
                    />
                )}
                {applyModal.render(<ApplyModal onOk={onApplyOk} width={610} />)}
            </div>
            {prevModal.render(
                <PreviewSourceModal data={SourceList?.data || []} />
            )}
        </Spin>
    );
};

export default Index;
